import { useState } from "react";
import { toast } from "sonner";
import { Instagram, MessageCircle, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useSite } from "../context/SiteContext";
import { Reveal } from "../components/Reveal";
import { useSEO } from "../lib/useSEO";

export default function Contato() {
  useSEO({ title: "Contato", description: "Parcerias, dúvidas sobre a mentoria ou uma reflexão para compartilhar." });
  const { site } = useSite();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/messages", form);
      const text = encodeURIComponent(`Olá! Sou ${form.name} (${form.email}). ${form.message}`);
      window.open(`https://wa.me/${site.whatsapp}?text=${text}`, "_blank");
      toast.success("Mensagem registrada. Abrimos o WhatsApp para você concluir o envio.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-12 lg:py-32" data-testid="contato-page">
      <div className="grid gap-16 lg:grid-cols-12">
        <Reveal className="lg:col-span-5">
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Contato</p>
          <h1 className="mt-6 font-serif text-5xl leading-tight text-zinc-100 sm:text-6xl">Vamos conversar.</h1>
          <p className="mt-8 max-w-md text-base leading-relaxed text-zinc-400">
            Parcerias, dúvidas sobre a mentoria ou apenas uma reflexão para compartilhar. Ao enviar, sua mensagem é registrada e o WhatsApp abre com o texto pronto.
          </p>
          <div className="mt-12 flex flex-col gap-4">
            <a href={site.instagram_url} target="_blank" rel="noreferrer" className="flex items-center gap-4 border border-gold/15 p-5 transition-all hover:-translate-y-0.5 hover:border-gold/40" data-testid="contact-instagram-link">
              <Instagram size={18} className="text-gold" />
              <span className="text-sm text-zinc-300">@refllexive no Instagram</span>
            </a>
            <a href={`https://wa.me/${site.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-4 border border-gold/15 p-5 transition-all hover:-translate-y-0.5 hover:border-gold/40" data-testid="contact-whatsapp-link">
              <MessageCircle size={18} className="text-gold" />
              <span className="text-sm text-zinc-300">WhatsApp direto</span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="lg:col-span-7">
          <form onSubmit={submit} className="border border-gold/15 bg-[#0a0a0a] p-8 lg:p-12" data-testid="contact-form">
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label className="mb-3 block text-xs font-semibold uppercase tracking-[0.2em] text-gold" htmlFor="contact-name">Nome</label>
                <input id="contact-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark" placeholder="Seu nome" data-testid="contact-name-input" />
              </div>
              <div>
                <label className="mb-3 block text-xs font-semibold uppercase tracking-[0.2em] text-gold" htmlFor="contact-email">E-mail</label>
                <input id="contact-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark" placeholder="voce@email.com" data-testid="contact-email-input" />
              </div>
            </div>
            <div className="mt-6">
              <label className="mb-3 block text-xs font-semibold uppercase tracking-[0.2em] text-gold" htmlFor="contact-message">Mensagem</label>
              <textarea id="contact-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-dark resize-none" placeholder="Escreva sua mensagem…" data-testid="contact-message-input" />
            </div>
            <button type="submit" disabled={loading} className="btn-gold mt-8" data-testid="contact-submit-button">
              {loading ? "Enviando…" : "Enviar mensagem"} <Send size={14} />
            </button>
          </form>
        </Reveal>
      </div>
    </div>
  );
}
